import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Logger } from '@utils/logger';
import { SubscriptionProductsService } from '@marketing/subscriptions-manage/services/subscription-products.service';
import { SimpleModalConfig } from '@frk/eds-components';
import {
  SelectorChip,
  GridHeader,
  GridRowData,
} from '@marketing/subscriptions-manage/subscription-manage.interface';
import { takeUntil } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@shared/translate/translate.service';

/**
 * Logger
 */
const logger = Logger.getLogger('ProductsModalComponent');

@Component({
  selector: 'ft-products-modal',
  templateUrl: './products-modal.component.html',
  styleUrls: ['./products-modal.component.scss'],
})
export class ProductsModalComponent implements OnInit, OnDestroy {
  private singleRowData: GridRowData;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private isEmailPreferences: boolean;
  public docTypeChips: SelectorChip[];
  public modalConfig: SimpleModalConfig;
  public isVisible$: Observable<boolean>;
  public isEmailPreferences$: Observable<boolean>;
  public shareClasses: SelectorChip[];
  public mockDocTypes: SelectorChip[];

  constructor(
    private subscriptionProductsService: SubscriptionProductsService,
    private translationService: TranslateService,
    @Inject(DOCUMENT) readonly documentRef: Document
  ) {
    this.modalConfig = {
      id: 'subscriptionsProductsModal',
    };
    // Mock testing data
    this.shareClasses = require('./__fixtures/shareclasses.mock.json');
    this.mockDocTypes =
      this.docTypeChips || require('./__fixtures/docTypes.mock.json');
  }

  public ngOnInit(): void {
    logger.debug('ModalComponent initialised');
    this.isVisible$ = this.subscriptionProductsService.getIsProductsModalVisible$();
    this.isEmailPreferences$ = this.subscriptionProductsService.getIsEmailPreferences$();
    this.isEmailPreferences$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isEmailPreferences: boolean) => {
        this.isEmailPreferences = isEmailPreferences;
        this.modalConfig.preTitle = this.translationService.instant(
          'subscriptions.edit-email-preferences'
        );
        // TODO: To cover email-preferences wizard.
      });
    this.subscriptionProductsService
      .getSingleRowData$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((singleRowData: GridRowData) => {
        const documentTypes = this.subscriptionProductsService.getDocumentTypes();
        this.singleRowData = singleRowData;
        this.modalConfig.preTitle = singleRowData.NAME;
        this.docTypeChips = documentTypes.map((type: GridHeader) => {
          return {
            id: type.id,
            selected: singleRowData[type.id],
            chips: [{ label: '', value: type.name || type.id }],
          };
        });
      });
  }

  /**
   * Close modal window
   */
  public onModalClose() {
    this.subscriptionProductsService.setIsProductsModalVisible$(false);
    this.subscriptionProductsService.setIsEmailPreferences$(false);
    this.documentRef.body.style.overflow = 'auto';
  }

  /**
   * Change document chips selection
   * @param $event - Event
   */
  public changeSelection($event: Event) {
    logger.debug('Change document selection');
    const chipClicked = this.docTypeChips.find((chip: SelectorChip) => {
      return chip.id === ($event.currentTarget as HTMLElement).id;
    });
    const chipIndex = this.docTypeChips.indexOf(chipClicked);
    this.docTypeChips[chipIndex].selected = !this.docTypeChips[chipIndex]
      .selected;
  }

  /**
   * Update documents selection in the table view
   */
  public updateDocs() {
    logger.debug('Update email preferences');
    if (this.isEmailPreferences) {
      // TODO: to configure email preferences submit.
      this.onModalClose();
      return;
    }
    logger.debug('Update documents');
    const changedDocuments = [];
    this.docTypeChips?.forEach((chip: SelectorChip) => {
      if (this.singleRowData[chip.id] !== chip.selected) {
        this.singleRowData[chip.id] = chip.selected;
        changedDocuments.push(`${this.singleRowData.ID}-${chip.id}`);
      }
    });
    this.subscriptionProductsService.setSingleRowData$(this.singleRowData);
    this.subscriptionProductsService.setRowDataChange$(changedDocuments);
    this.onModalClose();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
